'use client'

/**
 * LoginForm component for group portal authentication.
 *
 * Implements a two-step login flow matching Figma designs:
 *
 * 1. Email + Password authentication (first factor)
 * 2. Email OTP verification (second factor)
 */

import { useLoginFlow } from '@/hooks/useLoginFlow'
import { PasswordStepForm } from './PasswordStepForm'
import { OTPStepForm } from './OTPStepForm'

/** LoginForm component props */
interface LoginFormProps {
  /** Optional return URL to redirect after successful login */
  returnUrl?: string
  /** When true, skips OTP step (dev/QA only, requires ?test=true in URL) */
  testBypass?: boolean
}

/**
 * LoginForm component - Main orchestrator for the two-step login flow.
 *
 * Displays either the password step or OTP step based on the current flow
 * state. All state management is handled by the useLoginFlow hook, keeping this
 * component focused on rendering the appropriate step with the correct props.
 */
export function LoginForm({ returnUrl, testBypass }: LoginFormProps) {
  // Get all state and actions from custom hook
  const {
    currentStep,
    passwordState,
    resendState,
    passwordAction,
    otpAction,
    passwordError,
    otpError,
    passwordPending,
    otpPending,
    resendPending,
    handleResend,
    isResendDisabled,
    resendCooldown,
  } = useLoginFlow(returnUrl)

  // Login succeeded and navigation is under way; no form, so the consumed
  // single-use code cannot be submitted again.
  if (currentStep === 'success') {
    return (
      <p role="status" data-testid="info_signingIn">
        Signing you in...
      </p>
    )
  }

  // Render password step (email + password)
  if (currentStep === 'password') {
    return (
      <PasswordStepForm
        onSubmit={passwordAction}
        isSubmitting={passwordPending}
        error={passwordError}
        testBypass={testBypass}
      />
    )
  }

  // Render OTP verification step (6-digit code)
  return (
    <OTPStepForm
      email={passwordState?.email || ''}
      pendingAuthToken={passwordState?.pendingAuthToken || ''}
      onSubmit={otpAction}
      isSubmitting={otpPending}
      error={otpError}
      onResend={handleResend}
      isResendDisabled={isResendDisabled}
      resendCooldown={resendCooldown}
      resendError={resendState?.error}
      isResendPending={resendPending}
    />
  )
}
